// src/components/admin/PanelTestAfiliados.js
// Panel de gestión de afiliados de prueba. Solo SuperAdmin.
// Va colgado en el tab Integraciones (junto a T&C y config canje).

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Alert, Button, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Divider, IconButton, Tooltip,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  getRangoTest, listarTestAfiliados,
  crearBatchTest, limpiarTodoTest,
} from '../../services/testAfiliadosApi';

export default function PanelTestAfiliados({ onToast }) {
  const [rango, setRango]         = useState(null);
  const [afiliados, setAfiliados] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [dlgCrear, setDlgCrear]   = useState({ open: false, cantidad: 5, estadoInicial: 'EVALUACION', password: 'Test@1234' });
  const [dlgLimpiar, setDlgLimpiar] = useState({ open: false, confirmacion: '' });
  const [enCurso, setEnCurso]     = useState(false);
  const [ultimoBatch, setUltimoBatch] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [r, l] = await Promise.all([getRangoTest(), listarTestAfiliados()]);
      setRango(r);
      setAfiliados(l.afiliados || []);
    } catch (e) {
      onToast?.(e.message || 'No se pudo cargar.', 'error');
    } finally { setLoading(false); }
  }, [onToast]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleCrearBatch = async () => {
    setEnCurso(true);
    try {
      const r = await crearBatchTest({
        cantidad: parseInt(dlgCrear.cantidad, 10),
        estadoInicial: dlgCrear.estadoInicial,
        password: dlgCrear.password,
      });
      onToast?.(r.mensaje || 'Batch creado.', 'success');
      setUltimoBatch(r);
      setDlgCrear({ open: false, cantidad: 5, estadoInicial: 'EVALUACION', password: 'Test@1234' });
      await cargar();
    } catch (e) {
      onToast?.(e.message || 'Error al crear batch.', 'error');
    } finally { setEnCurso(false); }
  };

  const handleLimpiar = async () => {
    if (dlgLimpiar.confirmacion !== 'LIMPIAR') {
      onToast?.('Debe escribir LIMPIAR para confirmar.', 'warning');
      return;
    }
    setEnCurso(true);
    try {
      const r = await limpiarTodoTest();
      onToast?.(
        `${r.afiliadosEliminados} afiliados, ${r.usuariosEliminados} users, ${r.codigosCanjeEliminados} códigos eliminados.`,
        'success'
      );
      setDlgLimpiar({ open: false, confirmacion: '' });
      setUltimoBatch(null);
      await cargar();
    } catch (e) {
      onToast?.(e.message || 'Error al limpiar.', 'error');
    } finally { setEnCurso(false); }
  };

  const copiar = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => onToast?.(`Copiado: ${text}`, 'success'))
      .catch(() => onToast?.('No se pudo copiar.', 'error'));
  };

  const cupoUsado = afiliados.length;
  const cupoTotal = rango?.cupo || 100;
  const cupoDisponible = cupoTotal - cupoUsado;

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3, border: '2px dashed #c8a84b', bgcolor: '#fffbf0' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <ScienceIcon sx={{ color: '#c8a84b' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#8b6914' }}>
            Afiliados de prueba
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Mecanismo secreto de test — solo SuperAdmin
          </Typography>
        </Box>
        <Tooltip title="Recargar">
          <IconButton onClick={cargar} disabled={loading}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Rango reservado: cédulas <code>{rango?.rangoInicio?.toLocaleString()}</code> a{' '}
          <code>{rango?.rangoFin?.toLocaleString()}</code>
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Los documentos en este rango <strong>saltan la verificación OCR automáticamente</strong>. Puedes crear
          afiliados de prueba en batch, probar todo el flujo (aliados, canje, aprobación) y luego limpiar todo
          con un click sin afectar datos reales.
        </Typography>
      </Alert>

      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <Chip label={`${cupoUsado} / ${cupoTotal} usados`}
          color={cupoUsado === 0 ? 'default' : cupoDisponible > 20 ? 'success' : cupoDisponible > 0 ? 'warning' : 'error'}
          sx={{ fontWeight: 700 }} />
        <Button variant="contained" color="warning" startIcon={<AddIcon />}
          onClick={() => setDlgCrear({ open: true, cantidad: 5, estadoInicial: 'EVALUACION', password: 'Test@1234' })}
          disabled={cupoDisponible === 0}>
          Crear batch
        </Button>
        <Button variant="outlined" color="error" startIcon={<DeleteForeverIcon />}
          onClick={() => setDlgLimpiar({ open: true, confirmacion: '' })}
          disabled={cupoUsado === 0}>
          Limpiar todo
        </Button>
      </Stack>

      {ultimoBatch && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUltimoBatch(null)}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Último batch creado ({ultimoBatch.cantidadCreada} afiliados):
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '0.82rem' }}>
            {ultimoBatch.afiliados?.map((a) => (
              <li key={a.afiliadoID}>
                <code>{a.documento}</code> — {a.nombre} — <code>{a.email}</code> — pw: <code>{a.password}</code>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {loading && <CircularProgress size={20} />}

      {afiliados.length > 0 && (
        <TableContainer variant="outlined" component={Paper}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Cédula</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Coop</TableCell>
                <TableCell>Creado</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {afiliados.map(a => (
                <TableRow key={a.afiliadoID}>
                  <TableCell sx={{ fontSize: '0.72rem', color: '#888' }}>#{a.afiliadoID}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {a.documento}
                    <Chip label="TEST" size="small" color="warning" sx={{ ml: 1, fontSize: '0.6rem', height: 16 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.85rem' }}>{a.nombres} {a.apellidos}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {a.email}
                    <Tooltip title="Copiar email">
                      <IconButton size="small" onClick={() => copiar(a.email)}>
                        <ContentCopyIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip label={a.estado} size="small" sx={{ fontSize: '0.65rem' }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.72rem' }}>#{a.cooperativaID}</TableCell>
                  <TableCell sx={{ fontSize: '0.72rem' }}>
                    {a.fechaCreacion ? new Date(a.fechaCreacion).toLocaleDateString('es-CO') : '—'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Copiar cédula">
                      <IconButton size="small" onClick={() => copiar(a.documento)}>
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog crear */}
      <Dialog open={dlgCrear.open} onClose={() => setDlgCrear({ ...dlgCrear, open: false })} maxWidth="xs" fullWidth>
        <DialogTitle>Crear afiliados de prueba</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Crearemos usuarios con cédulas del rango reservado, password predecible y estado inicial elegido.
          </Alert>
          <Stack spacing={2}>
            <TextField label="Cantidad (1-20)" type="number" fullWidth
              value={dlgCrear.cantidad}
              onChange={e => setDlgCrear(p => ({ ...p, cantidad: e.target.value }))}
              inputProps={{ min: 1, max: 20 }} />
            <TextField label="Estado inicial" fullWidth select
              value={dlgCrear.estadoInicial}
              onChange={e => setDlgCrear(p => ({ ...p, estadoInicial: e.target.value }))}
              SelectProps={{ native: true }}>
              <option value="EVALUACION">EVALUACION</option>
              <option value="PENDIENTE">PENDIENTE</option>
              <option value="PENDIENTE_DOCUMENTOS">PENDIENTE_DOCUMENTOS</option>
              <option value="PENDIENTE_ORIENTACION">PENDIENTE_ORIENTACION</option>
              <option value="ACTIVO">ACTIVO</option>
            </TextField>
            <TextField label="Password" fullWidth
              value={dlgCrear.password}
              onChange={e => setDlgCrear(p => ({ ...p, password: e.target.value }))}
              helperText="Todos los users comparten esta contraseña (predecible por ser de test)" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgCrear({ ...dlgCrear, open: false })} disabled={enCurso}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={handleCrearBatch}
            disabled={enCurso || !dlgCrear.cantidad || parseInt(dlgCrear.cantidad, 10) < 1}
            startIcon={enCurso ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}>
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog limpiar */}
      <Dialog open={dlgLimpiar.open} onClose={() => setDlgLimpiar({ open: false, confirmacion: '' })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>⚠️ Limpiar TODOS los afiliados de prueba</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
              Esta acción es IRREVERSIBLE.
            </Typography>
            <Typography variant="body2">
              Se eliminarán en cascada:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
              <li>{cupoUsado} afiliado(s) del rango test</li>
              <li>Sus usuarios de Identity con todos sus roles</li>
              <li>Sus vínculos con cooperativas</li>
              <li>Todos sus códigos de canje asociados</li>
              <li>Todos los consumos de esos códigos</li>
            </Box>
          </Alert>
          <TextField
            label="Escribe LIMPIAR para confirmar"
            fullWidth
            value={dlgLimpiar.confirmacion}
            onChange={e => setDlgLimpiar(p => ({ ...p, confirmacion: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgLimpiar({ open: false, confirmacion: '' })} disabled={enCurso}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleLimpiar}
            disabled={enCurso || dlgLimpiar.confirmacion !== 'LIMPIAR'}
            startIcon={enCurso ? <CircularProgress size={14} color="inherit" /> : <DeleteForeverIcon />}>
            Limpiar todo
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
